import { DelegationAPI } from '@/lib/client/delegation-api';
import { DelegationContext, DelegationTarget, useDelegationStore } from '@/stores/delegation-store';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { useCallback, useEffect } from 'react';

// Query keys for delegation
export const delegationKeys = {
  all: ['delegation'] as const,
  targets: () => [...delegationKeys.all, 'targets'] as const,
};

// Hook to get available delegation targets
export function useAvailableDelegationTargets() {
  return useQuery({
    queryKey: delegationKeys.targets(),
    queryFn: DelegationAPI.getAvailableTargets,
    staleTime: 5 * 60 * 1000, // 5 minutes
    retry: 1, // Only retry once if it fails
    retryOnMount: false,
  });
}

// Main delegation management hook
export function useDelegation() {
  const {
    currentDelegation,
    isDelegating,
    availableTargets,
    setDelegation,
    clearDelegation,
    setAvailableTargets,
  } = useDelegationStore();
  
  const queryClient = useQueryClient();
  const { data: targets, isLoading, error } = useAvailableDelegationTargets();

  // Update available targets in store when query data changes
  useEffect(() => {
    if (targets) {
      setAvailableTargets(targets);
    }
  }, [targets, setAvailableTargets]);

  // Switch to a delegation target
  const switchTo = useCallback((target: DelegationTarget) => {
    const delegationContext: DelegationContext = {
      type: target.type,
      entityId: target.id,
      name: target.name,
    };
    
    setDelegation(delegationContext);
    
    // Invalidate all queries since we're changing context
    queryClient.invalidateQueries();
    
    toast.success(`Switched to ${target.name}`);
  }, [setDelegation, queryClient]);

  // Return to own account (clear delegation)
  const returnToMyAccount = useCallback(() => {
    const targetName = currentDelegation?.name;
    clearDelegation();
    
    // Invalidate all queries since we're changing context
    queryClient.invalidateQueries();
    
    if (targetName) {
      toast.info(`Returned from ${targetName} to your account`);
    }
  }, [clearDelegation, currentDelegation, queryClient]);

  // Get current context display text
  const getCurrentContextDisplay = useCallback(() => {
    if (!isDelegating || !currentDelegation) {
      return 'My Account';
    }
    
    const typeIcon = currentDelegation.type === 'organization' ? '📊' : '🏢';
    return `${typeIcon} ${currentDelegation.name}`;
  }, [isDelegating, currentDelegation]);

  return {
    // Current state
    currentDelegation,
    isDelegating,
    availableTargets: targets || [],
    isLoadingTargets: isLoading,
    targetsError: error,
    
    // Actions
    switchTo,
    returnToMyAccount,
    
    // Helpers
    getCurrentContextDisplay,
    
    // Computed properties
    canDelegate: (targets?.length || 0) > 0,
  };
}